import React, { useState, useEffect } from 'react';
import './StudentOutcomesDetails_Program_PLO.css';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { accessibleColors } from '../utils/contrastUtils';

// Register Chart.js components
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

// Function to convert percentage to letter grade
const getLetterGrade = (percentage) => {
  if (percentage >= 90) return 'A';
  if (percentage >= 80) return 'B';
  if (percentage >= 70) return 'C';
  if (percentage >= 60) return 'D';
  return 'F';
};

// Function to convert evidence scores to letter grades where possible
const formatEvidenceScore = (scoreStr) => {
  // Check if it ends with % which means it's a percentage
  if (scoreStr.endsWith('%')) {
    const numericScore = parseFloat(scoreStr.replace('%', ''));
    if (!isNaN(numericScore)) {
      return `${scoreStr} (${getLetterGrade(numericScore)})`;
    }
  }
  // If it's a fraction like 83/100
  else if (scoreStr.includes('/')) {
    const [numerator, denominator] = scoreStr.split('/');
    const numericScore = (parseFloat(numerator) / parseFloat(denominator)) * 100;
    if (!isNaN(numericScore)) {
      return `${scoreStr} (${getLetterGrade(numericScore)})`;
    }
  }
  // Return original if we can't parse it
  return scoreStr;
};

// Helper function to map course IDs to course names
const getCourseNameFromId = (courseId) => {
  // Map common course IDs to their names
  const courseNameMap = {
    'BS101': 'Introduction to Business Analytics',
    'BS102': 'Advanced Data Analysis',
    'BS103': 'Decision Making with Big Data',
    'BS110': 'Business Statistics',
    'BS201': 'Financial Accounting',
    'BS202': 'Managerial Accounting',
    'BS203': 'Marketing Principles',
    'BS204': 'Business Management',
    'BS301': 'Strategic Management',
    'BS302': 'Operations Management',
    'BS303': 'Business Ethics',
    'BS304': 'International Business',
    'BSM-BADM310': 'Principles of Management',
    'BSM-BADM311': 'Organizational Behavior',
    'BSM-BADM312': 'Business Leadership',
    'BSM-BADM313': 'Human Resource Management',
    'BSM-BADM314': 'Supply Chain Management'
  };
  
  return courseNameMap[courseId] || `Course ${courseId}`;
};

const StudentOutcomesDetails_Program_PLO = ({ selectedPLO, selectedProgram, onBack }) => {
  const [loading, setLoading] = useState(true);
  // Initialize with the first course from the related courses if available
  const [selectedCourse, setSelectedCourse] = useState(
    selectedPLO?.relatedCourses && selectedPLO.relatedCourses.length > 0
      ? (typeof selectedPLO.relatedCourses[0] === 'object' 
          ? selectedPLO.relatedCourses[0].course_code || selectedPLO.relatedCourses[0].code || selectedPLO.relatedCourses[0].id || 'BS101'
          : selectedPLO.relatedCourses[0])
      : 'BS101'
  );
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [activeTab, setActiveTab] = useState('performance');
  const [expandedLearningGoals, setExpandedLearningGoals] = useState({});
  const [showInfoText, setShowInfoText] = useState({});
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false); // New state for header expansion
  
  // New state for search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [studentsPerPage] = useState(5); // Number of students per page

  // Function to truncate text
  const truncateText = (text, maxLength = 60) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  // Mock data for demonstration - in a real app this would come from an API
  const coursesData = {
    'BSM-BADM310': {
      courseName: 'Principles of Management',
      stats: {
        achievementPercentage: 78,
        certificationImpact: "+18%"
      },
      students: [
        {
          id: 1,
          studentId: '09872384',
          name: 'Alex Johnson',
          status: 'exceed',
          score: 94,
          certifications: ['ACCA Passed', 'CMA Passed'],
          performance: {
            overallStatus: 'Exceed PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Management Principles', 
                score: 95, 
                status: 'exceed',
                keywords: ['Leadership', 'Planning', 'Organizing'],
                evidence: [
                  { type: 'Midterm', score: '94/100' },
                  { type: 'Final', score: '96/100' },
                  { type: 'Case Study Assignment', score: '92%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Team Management', 
                score: 92, 
                status: 'exceed',
                keywords: ['Team Building', 'Motivation', 'Delegation'],
                evidence: [
                  { type: 'Group Project', score: '94%' },
                  { type: 'Leadership Assessment', score: '90/100' },
                  { type: 'Team Evaluation', score: '95%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Business Strategy', 
                score: 93, 
                status: 'exceed',
                keywords: ['Strategic Planning', 'Market Analysis', 'SWOT Analysis'],
                evidence: [
                  { type: 'Strategy Report', score: '94%' },
                  { type: 'Business Plan', score: '92%' },
                  { type: 'Final Exam', score: '91/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Management Ethics', 
                score: 96, 
                status: 'exceed',
                keywords: ['Ethical Leadership', 'Corporate Responsibility', 'Governance'],
                evidence: [
                  { type: 'Ethics Case Study', score: '97%' },
                  { type: 'Ethics Paper', score: '95%' },
                  { type: 'Group Presentation', score: '94%' }
                ]
              }
            ]
          },
          skills: {
            'Leadership': 0.94,
            'Decision Making': 0.92,
            'Strategic Planning': 0.90,
            'Team Management': 0.95,
            'Communication': 0.88,
            'Problem Solving': 0.91
          }
        },
        {
          id: 2,
          studentId: '09872385',
          name: 'Sam Williams',
          status: 'meet',
          score: 85,
          certifications: ['CFA Passed'],
          performance: {
            overallStatus: 'Meet PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Management Principles', 
                score: 83, 
                status: 'meet',
                keywords: ['Leadership', 'Planning', 'Organizing'],
                evidence: [
                  { type: 'Midterm', score: '82/100' },
                  { type: 'Final', score: '85/100' },
                  { type: 'Case Study Assignment', score: '84%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Team Management', 
                score: 87, 
                status: 'meet',
                keywords: ['Team Building', 'Motivation', 'Delegation'],
                evidence: [
                  { type: 'Group Project', score: '86%' },
                  { type: 'Leadership Assessment', score: '85/100' },
                  { type: 'Team Evaluation', score: '87%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Business Strategy', 
                score: 85, 
                status: 'meet',
                keywords: ['Strategic Planning', 'Market Analysis', 'SWOT Analysis'],
                evidence: [
                  { type: 'Strategy Report', score: '84%' },
                  { type: 'Business Plan', score: '86%' },
                  { type: 'Final Exam', score: '85/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Management Ethics', 
                score: 84, 
                status: 'meet',
                keywords: ['Ethical Leadership', 'Corporate Responsibility', 'Governance'],
                evidence: [
                  { type: 'Ethics Case Study', score: '85%' },
                  { type: 'Ethics Paper', score: '83%' },
                  { type: 'Group Presentation', score: '86%' }
                ]
              }
            ]
          },
          skills: {
            'Leadership': 0.84,
            'Decision Making': 0.82,
            'Strategic Planning': 0.80,
            'Team Management': 0.87,
            'Communication': 0.85,
            'Problem Solving': 0.83
          }
        },
        {
          id: 3,
          studentId: '09872386',
          name: 'Jamie Lee',
          status: 'atRisk',
          score: 65,
          certifications: ['ACCA Pending'],
          performance: {
            overallStatus: 'At Risk',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Management Principles', 
                score: 63, 
                status: 'atRisk',
                keywords: ['Leadership', 'Planning', 'Organizing'],
                evidence: [
                  { type: 'Midterm', score: '64/100' },
                  { type: 'Final', score: '63/100' },
                  { type: 'Case Study Assignment', score: '65%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Team Management', 
                score: 67, 
                status: 'atRisk',
                keywords: ['Team Building', 'Motivation', 'Delegation'],
                evidence: [
                  { type: 'Group Project', score: '68%' },
                  { type: 'Leadership Assessment', score: '64/100' },
                  { type: 'Team Evaluation', score: '66%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Business Strategy', 
                score: 62, 
                status: 'atRisk',
                keywords: ['Strategic Planning', 'Market Analysis', 'SWOT Analysis'],
                evidence: [
                  { type: 'Strategy Report', score: '61%' },
                  { type: 'Business Plan', score: '63%' },
                  { type: 'Final Exam', score: '62/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Management Ethics', 
                score: 66, 
                status: 'atRisk',
                keywords: ['Ethical Leadership', 'Corporate Responsibility', 'Governance'],
                evidence: [
                  { type: 'Ethics Case Study', score: '65%' },
                  { type: 'Ethics Paper', score: '67%' },
                  { type: 'Group Presentation', score: '64%' }
                ]
              }
            ]
          },
          skills: {
            'Leadership': 0.64,
            'Decision Making': 0.62,
            'Strategic Planning': 0.61,
            'Team Management': 0.67,
            'Communication': 0.70,
            'Problem Solving': 0.63
          }
        },
        {
          id: 4,
          studentId: '09872387',
          name: 'Taylor Rodriguez',
          status: 'meet',
          score: 78,
          certifications: ['CMA Passed'],
          performance: {
            overallStatus: 'Meet PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Management Principles', 
                score: 76, 
                status: 'meet',
                keywords: ['Leadership', 'Planning', 'Organizing'],
                evidence: [
                  { type: 'Midterm', score: '76/100' },
                  { type: 'Final', score: '77/100' },
                  { type: 'Case Study Assignment', score: '75%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Team Management', 
                score: 79, 
                status: 'meet',
                keywords: ['Team Building', 'Motivation', 'Delegation'],
                evidence: [
                  { type: 'Group Project', score: '80%' },
                  { type: 'Leadership Assessment', score: '78/100' },
                  { type: 'Team Evaluation', score: '79%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Business Strategy', 
                score: 77, 
                status: 'meet',
                keywords: ['Strategic Planning', 'Market Analysis', 'SWOT Analysis'],
                evidence: [
                  { type: 'Strategy Report', score: '76%' },
                  { type: 'Business Plan', score: '78%' },
                  { type: 'Final Exam', score: '77/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Management Ethics', 
                score: 80, 
                status: 'meet',
                keywords: ['Ethical Leadership', 'Corporate Responsibility', 'Governance'],
                evidence: [
                  { type: 'Ethics Case Study', score: '81%' },
                  { type: 'Ethics Paper', score: '78%' },
                  { type: 'Group Presentation', score: '80%' }
                ]
              }
            ]
          },
          skills: {
            'Leadership': 0.78,
            'Decision Making': 0.77,
            'Strategic Planning': 0.76,
            'Team Management': 0.79,
            'Communication': 0.82,
            'Problem Solving': 0.75
          }
        },
        {
          id: 5,
          studentId: '09872388',
          name: 'Morgan Chen',
          status: 'exceed',
          score: 91,
          certifications: ['CFA Passed', 'ACCA Passed'],
          performance: {
            overallStatus: 'Exceed PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Management Principles', 
                score: 90, 
                status: 'exceed',
                keywords: ['Leadership', 'Planning', 'Organizing'],
                evidence: [
                  { type: 'Midterm', score: '89/100' },
                  { type: 'Final', score: '92/100' },
                  { type: 'Case Study Assignment', score: '90%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Team Management', 
                score: 93, 
                status: 'exceed',
                keywords: ['Team Building', 'Motivation', 'Delegation'],
                evidence: [
                  { type: 'Group Project', score: '94%' },
                  { type: 'Leadership Assessment', score: '92/100' },
                  { type: 'Team Evaluation', score: '93%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Business Strategy', 
                score: 92, 
                status: 'exceed',
                keywords: ['Strategic Planning', 'Market Analysis', 'SWOT Analysis'],
                evidence: [
                  { type: 'Strategy Report', score: '91%' },
                  { type: 'Business Plan', score: '93%' },
                  { type: 'Final Exam', score: '92/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Management Ethics', 
                score: 90, 
                status: 'exceed',
                keywords: ['Ethical Leadership', 'Corporate Responsibility', 'Governance'],
                evidence: [
                  { type: 'Ethics Case Study', score: '89%' },
                  { type: 'Ethics Paper', score: '91%' },
                  { type: 'Group Presentation', score: '90%' }
                ]
              }
            ]
          },
          skills: {
            'Leadership': 0.91,
            'Decision Making': 0.90,
            'Strategic Planning': 0.92,
            'Team Management': 0.93,
            'Communication': 0.88,
            'Problem Solving': 0.89
          }
        }
      ]
    },
    'BSM-BADM311': {
      courseName: 'Organizational Behavior',
      stats: {
        achievementPercentage: 75,
        certificationImpact: "+15%"
      },
      students: [
        {
          id: 1,
          studentId: '09872384',
          name: 'Alex Johnson',
          status: 'meet',
          score: 88,
          certifications: ['ACCA Passed', 'SHRM Certified'],
          performance: {
            overallStatus: 'Meet PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Organizational Structure', 
                score: 87, 
                status: 'meet',
                keywords: ['Hierarchies', 'Teams', 'Networks'],
                evidence: [
                  { type: 'Midterm', score: '86/100' },
                  { type: 'Final', score: '89/100' },
                  { type: 'Structure Analysis', score: '87%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Cultural Dynamics', 
                score: 90, 
                status: 'exceed',
                keywords: ['Culture Assessment', 'Change Management', 'Diversity'],
                evidence: [
                  { type: 'Cultural Analysis', score: '92%' },
                  { type: 'Diversity Case Study', score: '88/100' },
                  { type: 'Group Project', score: '90%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Motivation Theories', 
                score: 86, 
                status: 'meet',
                keywords: ['Intrinsic Motivation', 'Extrinsic Motivation', 'Incentives'],
                evidence: [
                  { type: 'Theory Paper', score: '85%' },
                  { type: 'Case Analysis', score: '87%' },
                  { type: 'Final Exam', score: '86/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Conflict Resolution', 
                score: 89, 
                status: 'meet',
                keywords: ['Negotiation', 'Mediation', 'Conflict Management'],
                evidence: [
                  { type: 'Role Play Assessment', score: '88%' },
                  { type: 'Conflict Resolution Plan', score: '90%' },
                  { type: 'Case Study', score: '89%' }
                ]
              }
            ]
          },
          skills: {
            'Communication': 0.89,
            'Conflict Resolution': 0.87,
            'Cultural Awareness': 0.92,
            'Team Management': 0.85,
            'Emotional Intelligence': 0.90,
            'Change Management': 0.86
          }
        },
        {
          id: 2,
          studentId: '09872385',
          name: 'Sam Williams',
          status: 'exceed',
          score: 92,
          certifications: ['CFA Passed', 'SHRM Certified'],
          performance: {
            overallStatus: 'Exceed PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Organizational Structure', 
                score: 91, 
                status: 'exceed',
                keywords: ['Hierarchies', 'Teams', 'Networks'],
                evidence: [
                  { type: 'Midterm', score: '90/100' },
                  { type: 'Final', score: '92/100' },
                  { type: 'Structure Analysis', score: '91%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Cultural Dynamics', 
                score: 94, 
                status: 'exceed',
                keywords: ['Culture Assessment', 'Change Management', 'Diversity'],
                evidence: [
                  { type: 'Cultural Analysis', score: '95%' },
                  { type: 'Diversity Case Study', score: '93/100' },
                  { type: 'Group Project', score: '94%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Motivation Theories', 
                score: 92, 
                status: 'exceed',
                keywords: ['Intrinsic Motivation', 'Extrinsic Motivation', 'Incentives'],
                evidence: [
                  { type: 'Theory Paper', score: '91%' },
                  { type: 'Case Analysis', score: '93%' },
                  { type: 'Final Exam', score: '92/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Conflict Resolution', 
                score: 91, 
                status: 'exceed',
                keywords: ['Negotiation', 'Mediation', 'Conflict Management'],
                evidence: [
                  { type: 'Role Play Assessment', score: '90%' },
                  { type: 'Conflict Resolution Plan', score: '92%' },
                  { type: 'Case Study', score: '91%' }
                ]
              }
            ]
          },
          skills: {
            'Communication': 0.93,
            'Conflict Resolution': 0.92,
            'Cultural Awareness': 0.90,
            'Team Management': 0.91,
            'Emotional Intelligence': 0.94,
            'Change Management': 0.90
          }
        },
        {
          id: 3,
          studentId: '09872386',
          name: 'Jamie Lee',
          status: 'meet',
          score: 78,
          certifications: ['SHRM Pending'],
          performance: {
            overallStatus: 'Meet PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Organizational Structure', 
                score: 77, 
                status: 'meet',
                keywords: ['Hierarchies', 'Teams', 'Networks'],
                evidence: [
                  { type: 'Midterm', score: '76/100' },
                  { type: 'Final', score: '79/100' },
                  { type: 'Structure Analysis', score: '77%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Cultural Dynamics', 
                score: 80, 
                status: 'meet',
                keywords: ['Culture Assessment', 'Change Management', 'Diversity'],
                evidence: [
                  { type: 'Cultural Analysis', score: '81%' },
                  { type: 'Diversity Case Study', score: '78/100' },
                  { type: 'Group Project', score: '82%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Motivation Theories', 
                score: 76, 
                status: 'meet',
                keywords: ['Intrinsic Motivation', 'Extrinsic Motivation', 'Incentives'],
                evidence: [
                  { type: 'Theory Paper', score: '75%' },
                  { type: 'Case Analysis', score: '77%' },
                  { type: 'Final Exam', score: '76/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Conflict Resolution', 
                score: 78, 
                status: 'meet',
                keywords: ['Negotiation', 'Mediation', 'Conflict Management'],
                evidence: [
                  { type: 'Role Play Assessment', score: '77%' },
                  { type: 'Conflict Resolution Plan', score: '79%' },
                  { type: 'Case Study', score: '78%' }
                ]
              }
            ]
          },
          skills: {
            'Communication': 0.80,
            'Conflict Resolution': 0.78,
            'Cultural Awareness': 0.75,
            'Team Management': 0.76,
            'Emotional Intelligence': 0.82,
            'Change Management': 0.75
          }
        },
        {
          id: 4,
          studentId: '09872387',
          name: 'Taylor Rodriguez',
          status: 'atRisk',
          score: 65,
          certifications: ['None'],
          performance: {
            overallStatus: 'At Risk',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Organizational Structure', 
                score: 64, 
                status: 'atRisk',
                keywords: ['Hierarchies', 'Teams', 'Networks'],
                evidence: [
                  { type: 'Midterm', score: '63/100' },
                  { type: 'Final', score: '66/100' },
                  { type: 'Structure Analysis', score: '64%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Cultural Dynamics', 
                score: 67, 
                status: 'atRisk',
                keywords: ['Culture Assessment', 'Change Management', 'Diversity'],
                evidence: [
                  { type: 'Cultural Analysis', score: '68%' },
                  { type: 'Diversity Case Study', score: '65/100' },
                  { type: 'Group Project', score: '67%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Motivation Theories', 
                score: 63, 
                status: 'atRisk',
                keywords: ['Intrinsic Motivation', 'Extrinsic Motivation', 'Incentives'],
                evidence: [
                  { type: 'Theory Paper', score: '62%' },
                  { type: 'Case Analysis', score: '64%' },
                  { type: 'Final Exam', score: '63/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Conflict Resolution', 
                score: 68, 
                status: 'atRisk',
                keywords: ['Negotiation', 'Mediation', 'Conflict Management'],
                evidence: [
                  { type: 'Role Play Assessment', score: '67%' },
                  { type: 'Conflict Resolution Plan', score: '69%' },
                  { type: 'Case Study', score: '68%' }
                ]
              }
            ]
          },
          skills: {
            'Communication': 0.67,
            'Conflict Resolution': 0.68,
            'Cultural Awareness': 0.63,
            'Team Management': 0.64,
            'Emotional Intelligence': 0.66,
            'Change Management': 0.65
          }
        },
        {
          id: 5,
          studentId: '09872388',
          name: 'Morgan Chen',
          status: 'meet',
          score: 82,
          certifications: ['SHRM Certified'],
          performance: {
            overallStatus: 'Meet PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Organizational Structure', 
                score: 81, 
                status: 'meet',
                keywords: ['Hierarchies', 'Teams', 'Networks'],
                evidence: [
                  { type: 'Midterm', score: '80/100' },
                  { type: 'Final', score: '82/100' },
                  { type: 'Structure Analysis', score: '81%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Cultural Dynamics', 
                score: 84, 
                status: 'meet',
                keywords: ['Culture Assessment', 'Change Management', 'Diversity'],
                evidence: [
                  { type: 'Cultural Analysis', score: '85%' },
                  { type: 'Diversity Case Study', score: '83/100' },
                  { type: 'Group Project', score: '84%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Motivation Theories', 
                score: 80, 
                status: 'meet',
                keywords: ['Intrinsic Motivation', 'Extrinsic Motivation', 'Incentives'],
                evidence: [
                  { type: 'Theory Paper', score: '79%' },
                  { type: 'Case Analysis', score: '81%' },
                  { type: 'Final Exam', score: '80/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Conflict Resolution', 
                score: 83, 
                status: 'meet',
                keywords: ['Negotiation', 'Mediation', 'Conflict Management'],
                evidence: [
                  { type: 'Role Play Assessment', score: '82%' },
                  { type: 'Conflict Resolution Plan', score: '84%' },
                  { type: 'Case Study', score: '83%' }
                ]
              }
            ]
          },
          skills: {
            'Communication': 0.83,
            'Conflict Resolution': 0.84,
            'Cultural Awareness': 0.81,
            'Team Management': 0.80,
            'Emotional Intelligence': 0.85,
            'Change Management': 0.82
          }
        }
      ]
    },
    'BSM-BADM312': {
      courseName: 'Business Leadership',
      stats: {
        achievementPercentage: 80,
        certificationImpact: "+20%"
      },
      students: [
        {
          id: 1,
          studentId: '09872384',
          name: 'Alex Johnson',
          status: 'exceed',
          score: 90,
          certifications: ['ACCA Passed', 'Leadership Certified'],
          performance: {
            overallStatus: 'Exceed PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Leadership Styles', 
                score: 92, 
                status: 'exceed',
                keywords: ['Transformational', 'Servant Leadership', 'Situational'],
                evidence: [
                  { type: 'Style Analysis', score: '91/100' },
                  { type: 'Case Study', score: '93/100' },
                  { type: 'Leadership Assessment', score: '92%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Strategic Leadership', 
                score: 89, 
                status: 'meet',
                keywords: ['Vision', 'Mission', 'Strategy Implementation'],
                evidence: [
                  { type: 'Strategy Paper', score: '87%' },
                  { type: 'Business Simulation', score: '90/100' },
                  { type: 'Group Project', score: '89%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Change Leadership', 
                score: 90, 
                status: 'exceed',
                keywords: ['Change Management', 'Resistance', 'Transformation'],
                evidence: [
                  { type: 'Change Plan', score: '91%' },
                  { type: 'Case Analysis', score: '89%' },
                  { type: 'Final Exam', score: '90/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Ethical Leadership', 
                score: 93, 
                status: 'exceed',
                keywords: ['Ethics', 'Governance', 'Social Responsibility'],
                evidence: [
                  { type: 'Ethics Case Study', score: '94%' },
                  { type: 'Ethics Paper', score: '92%' },
                  { type: 'Presentation', score: '93%' }
                ]
              }
            ]
          },
          skills: {
            'Leadership': 0.91,
            'Decision Making': 0.90,
            'Strategic Vision': 0.89,
            'Team Building': 0.92,
            'Change Management': 0.88,
            'Ethical Reasoning': 0.93
          }
        },
        {
          id: 2,
          studentId: '09872385',
          name: 'Sam Williams',
          status: 'meet',
          score: 83,
          certifications: ['Leadership Certified'],
          performance: {
            overallStatus: 'Meet PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Leadership Styles', 
                score: 84, 
                status: 'meet',
                keywords: ['Transformational', 'Servant Leadership', 'Situational'],
                evidence: [
                  { type: 'Style Analysis', score: '82/100' },
                  { type: 'Case Study', score: '85/100' },
                  { type: 'Leadership Assessment', score: '84%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Strategic Leadership', 
                score: 82, 
                status: 'meet',
                keywords: ['Vision', 'Mission', 'Strategy Implementation'],
                evidence: [
                  { type: 'Strategy Paper', score: '81%' },
                  { type: 'Business Simulation', score: '83/100' },
                  { type: 'Group Project', score: '82%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Change Leadership', 
                score: 83, 
                status: 'meet',
                keywords: ['Change Management', 'Resistance', 'Transformation'],
                evidence: [
                  { type: 'Change Plan', score: '84%' },
                  { type: 'Case Analysis', score: '82%' },
                  { type: 'Final Exam', score: '83/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Ethical Leadership', 
                score: 85, 
                status: 'meet',
                keywords: ['Ethics', 'Governance', 'Social Responsibility'],
                evidence: [
                  { type: 'Ethics Case Study', score: '86%' },
                  { type: 'Ethics Paper', score: '84%' },
                  { type: 'Presentation', score: '85%' }
                ]
              }
            ]
          },
          skills: {
            'Leadership': 0.84,
            'Decision Making': 0.82,
            'Strategic Vision': 0.81,
            'Team Building': 0.85,
            'Change Management': 0.83,
            'Ethical Reasoning': 0.86
          }
        },
        {
          id: 3,
          studentId: '09872386',
          name: 'Jamie Lee',
          status: 'atRisk',
          score: 68,
          certifications: ['None'],
          performance: {
            overallStatus: 'At Risk',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Leadership Styles', 
                score: 67, 
                status: 'atRisk',
                keywords: ['Transformational', 'Servant Leadership', 'Situational'],
                evidence: [
                  { type: 'Style Analysis', score: '66/100' },
                  { type: 'Case Study', score: '68/100' },
                  { type: 'Leadership Assessment', score: '67%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Strategic Leadership', 
                score: 65, 
                status: 'atRisk',
                keywords: ['Vision', 'Mission', 'Strategy Implementation'],
                evidence: [
                  { type: 'Strategy Paper', score: '64%' },
                  { type: 'Business Simulation', score: '67/100' },
                  { type: 'Group Project', score: '65%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Change Leadership', 
                score: 69, 
                status: 'atRisk',
                keywords: ['Change Management', 'Resistance', 'Transformation'],
                evidence: [
                  { type: 'Change Plan', score: '70%' },
                  { type: 'Case Analysis', score: '68%' },
                  { type: 'Final Exam', score: '69/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Ethical Leadership', 
                score: 70, 
                status: 'meet',
                keywords: ['Ethics', 'Governance', 'Social Responsibility'],
                evidence: [
                  { type: 'Ethics Case Study', score: '71%' },
                  { type: 'Ethics Paper', score: '69%' },
                  { type: 'Presentation', score: '70%' }
                ]
              }
            ]
          },
          skills: {
            'Leadership': 0.68,
            'Decision Making': 0.65,
            'Strategic Vision': 0.64,
            'Team Building': 0.70,
            'Change Management': 0.69,
            'Ethical Reasoning': 0.71
          }
        },
        {
          id: 4,
          studentId: '09872387',
          name: 'Taylor Rodriguez',
          status: 'meet',
          score: 72,
          certifications: ['Leadership In Progress'],
          performance: {
            overallStatus: 'Meet PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Leadership Styles', 
                score: 73, 
                status: 'meet',
                keywords: ['Transformational', 'Servant Leadership', 'Situational'],
                evidence: [
                  { type: 'Style Analysis', score: '72/100' },
                  { type: 'Case Study', score: '74/100' },
                  { type: 'Leadership Assessment', score: '73%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Strategic Leadership', 
                score: 70, 
                status: 'meet',
                keywords: ['Vision', 'Mission', 'Strategy Implementation'],
                evidence: [
                  { type: 'Strategy Paper', score: '69%' },
                  { type: 'Business Simulation', score: '72/100' },
                  { type: 'Group Project', score: '70%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Change Leadership', 
                score: 71, 
                status: 'meet',
                keywords: ['Change Management', 'Resistance', 'Transformation'],
                evidence: [
                  { type: 'Change Plan', score: '72%' },
                  { type: 'Case Analysis', score: '70%' },
                  { type: 'Final Exam', score: '71/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Ethical Leadership', 
                score: 73, 
                status: 'meet',
                keywords: ['Ethics', 'Governance', 'Social Responsibility'],
                evidence: [
                  { type: 'Ethics Case Study', score: '74%' },
                  { type: 'Ethics Paper', score: '72%' },
                  { type: 'Presentation', score: '73%' }
                ]
              }
            ]
          },
          skills: {
            'Leadership': 0.72,
            'Decision Making': 0.70,
            'Strategic Vision': 0.71,
            'Team Building': 0.73,
            'Change Management': 0.71,
            'Ethical Reasoning': 0.74
          }
        },
        {
          id: 5,
          studentId: '09872388',
          name: 'Morgan Chen',
          status: 'exceed',
          score: 95,
          certifications: ['Leadership Certified', 'ACCA Passed'],
          performance: {
            overallStatus: 'Exceed PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Leadership Styles', 
                score: 96, 
                status: 'exceed',
                keywords: ['Transformational', 'Servant Leadership', 'Situational'],
                evidence: [
                  { type: 'Style Analysis', score: '95/100' },
                  { type: 'Case Study', score: '97/100' },
                  { type: 'Leadership Assessment', score: '96%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Strategic Leadership', 
                score: 94, 
                status: 'exceed',
                keywords: ['Vision', 'Mission', 'Strategy Implementation'],
                evidence: [
                  { type: 'Strategy Paper', score: '93%' },
                  { type: 'Business Simulation', score: '95/100' },
                  { type: 'Group Project', score: '94%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Change Leadership', 
                score: 95, 
                status: 'exceed',
                keywords: ['Change Management', 'Resistance', 'Transformation'],
                evidence: [
                  { type: 'Change Plan', score: '96%' },
                  { type: 'Case Analysis', score: '94%' },
                  { type: 'Final Exam', score: '95/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Ethical Leadership', 
                score: 97, 
                status: 'exceed',
                keywords: ['Ethics', 'Governance', 'Social Responsibility'],
                evidence: [
                  { type: 'Ethics Case Study', score: '98%' },
                  { type: 'Ethics Paper', score: '96%' },
                  { type: 'Presentation', score: '97%' }
                ]
              }
            ]
          },
          skills: {
            'Leadership': 0.96,
            'Decision Making': 0.94,
            'Strategic Vision': 0.95,
            'Team Building': 0.97,
            'Change Management': 0.94,
            'Ethical Reasoning': 0.98
          }
        }
      ]
    },
    'BSM-BADM313': {
      courseName: 'Human Resource Management',
      stats: {
        achievementPercentage: 76,
        certificationImpact: "+14%"
      },
      students: [
        {
          id: 1,
          studentId: '09872384',
          name: 'Alex Johnson',
          status: 'meet',
          score: 81,
          certifications: ['SHRM Passed'],
          performance: {
            overallStatus: 'Meet PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Recruitment & Selection', 
                score: 83, 
                status: 'meet',
                keywords: ['Hiring', 'Interviewing', 'Selection Methods'],
                evidence: [
                  { type: 'Recruitment Strategy', score: '82/100' },
                  { type: 'Interview Assignment', score: '84/100' },
                  { type: 'Case Study', score: '83%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Performance Management', 
                score: 80, 
                status: 'meet',
                keywords: ['Evaluation', 'Feedback', 'Performance Improvement'],
                evidence: [
                  { type: 'Evaluation System Design', score: '79%' },
                  { type: 'Feedback Exercise', score: '81/100' },
                  { type: 'Case Analysis', score: '80%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Compensation & Benefits', 
                score: 82, 
                status: 'meet',
                keywords: ['Salary', 'Benefits', 'Incentives'],
                evidence: [
                  { type: 'Compensation Plan', score: '83%' },
                  { type: 'Benefits Analysis', score: '81%' },
                  { type: 'Final Exam', score: '82/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Employment Law', 
                score: 80, 
                status: 'meet',
                keywords: ['Legal Compliance', 'Regulations', 'Employee Rights'],
                evidence: [
                  { type: 'Legal Analysis', score: '79%' },
                  { type: 'Compliance Report', score: '81%' },
                  { type: 'Case Law Review', score: '80%' }
                ]
              }
            ]
          },
          skills: {
            'Recruiting': 0.83,
            'Performance Evaluation': 0.80,
            'Compensation Planning': 0.82,
            'Legal Compliance': 0.80,
            'Employee Relations': 0.81,
            'Training & Development': 0.79
          }
        },
        {
          id: 2,
          studentId: '09872385',
          name: 'Sam Williams',
          status: 'atRisk',
          score: 68,
          certifications: ['None'],
          performance: {
            overallStatus: 'At Risk',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Recruitment & Selection', 
                score: 69, 
                status: 'atRisk',
                keywords: ['Hiring', 'Interviewing', 'Selection Methods'],
                evidence: [
                  { type: 'Recruitment Strategy', score: '68/100' },
                  { type: 'Interview Assignment', score: '70/100' },
                  { type: 'Case Study', score: '69%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Performance Management', 
                score: 67, 
                status: 'atRisk',
                keywords: ['Evaluation', 'Feedback', 'Performance Improvement'],
                evidence: [
                  { type: 'Evaluation System Design', score: '66%' },
                  { type: 'Feedback Exercise', score: '68/100' },
                  { type: 'Case Analysis', score: '67%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Compensation & Benefits', 
                score: 68, 
                status: 'atRisk',
                keywords: ['Salary', 'Benefits', 'Incentives'],
                evidence: [
                  { type: 'Compensation Plan', score: '69%' },
                  { type: 'Benefits Analysis', score: '67%' },
                  { type: 'Final Exam', score: '68/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Employment Law', 
                score: 70, 
                status: 'meet',
                keywords: ['Legal Compliance', 'Regulations', 'Employee Rights'],
                evidence: [
                  { type: 'Legal Analysis', score: '71%' },
                  { type: 'Compliance Report', score: '69%' },
                  { type: 'Case Law Review', score: '70%' }
                ]
              }
            ]
          },
          skills: {
            'Recruiting': 0.69,
            'Performance Evaluation': 0.67,
            'Compensation Planning': 0.68,
            'Legal Compliance': 0.70,
            'Employee Relations': 0.67,
            'Training & Development': 0.66
          }
        },
        {
          id: 3,
          studentId: '09872386',
          name: 'Jamie Lee',
          status: 'meet',
          score: 76,
          certifications: ['SHRM In Progress'],
          performance: {
            overallStatus: 'Meet PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Recruitment & Selection', 
                score: 77, 
                status: 'meet',
                keywords: ['Hiring', 'Interviewing', 'Selection Methods'],
                evidence: [
                  { type: 'Recruitment Strategy', score: '76/100' },
                  { type: 'Interview Assignment', score: '78/100' },
                  { type: 'Case Study', score: '77%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Performance Management', 
                score: 75, 
                status: 'meet',
                keywords: ['Evaluation', 'Feedback', 'Performance Improvement'],
                evidence: [
                  { type: 'Evaluation System Design', score: '74%' },
                  { type: 'Feedback Exercise', score: '76/100' },
                  { type: 'Case Analysis', score: '75%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Compensation & Benefits', 
                score: 76, 
                status: 'meet',
                keywords: ['Salary', 'Benefits', 'Incentives'],
                evidence: [
                  { type: 'Compensation Plan', score: '77%' },
                  { type: 'Benefits Analysis', score: '75%' },
                  { type: 'Final Exam', score: '76/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Employment Law', 
                score: 77, 
                status: 'meet',
                keywords: ['Legal Compliance', 'Regulations', 'Employee Rights'],
                evidence: [
                  { type: 'Legal Analysis', score: '78%' },
                  { type: 'Compliance Report', score: '76%' },
                  { type: 'Case Law Review', score: '77%' }
                ]
              }
            ]
          },
          skills: {
            'Recruiting': 0.77,
            'Performance Evaluation': 0.75,
            'Compensation Planning': 0.76,
            'Legal Compliance': 0.77,
            'Employee Relations': 0.74,
            'Training & Development': 0.75
          }
        },
        {
          id: 4,
          studentId: '09872387',
          name: 'Taylor Rodriguez',
          status: 'exceed',
          score: 90,
          certifications: ['SHRM Passed', 'HR Certification'],
          performance: {
            overallStatus: 'Exceed PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Recruitment & Selection', 
                score: 91, 
                status: 'exceed',
                keywords: ['Hiring', 'Interviewing', 'Selection Methods'],
                evidence: [
                  { type: 'Recruitment Strategy', score: '90/100' },
                  { type: 'Interview Assignment', score: '92/100' },
                  { type: 'Case Study', score: '91%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Performance Management', 
                score: 89, 
                status: 'meet',
                keywords: ['Evaluation', 'Feedback', 'Performance Improvement'],
                evidence: [
                  { type: 'Evaluation System Design', score: '88%' },
                  { type: 'Feedback Exercise', score: '90/100' },
                  { type: 'Case Analysis', score: '89%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Compensation & Benefits', 
                score: 90, 
                status: 'exceed',
                keywords: ['Salary', 'Benefits', 'Incentives'],
                evidence: [
                  { type: 'Compensation Plan', score: '91%' },
                  { type: 'Benefits Analysis', score: '89%' },
                  { type: 'Final Exam', score: '90/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Employment Law', 
                score: 92, 
                status: 'exceed',
                keywords: ['Legal Compliance', 'Regulations', 'Employee Rights'],
                evidence: [
                  { type: 'Legal Analysis', score: '93%' },
                  { type: 'Compliance Report', score: '91%' },
                  { type: 'Case Law Review', score: '92%' }
                ]
              }
            ]
          },
          skills: {
            'Recruiting': 0.91,
            'Performance Evaluation': 0.89,
            'Compensation Planning': 0.90,
            'Legal Compliance': 0.92,
            'Employee Relations': 0.88,
            'Training & Development': 0.89
          }
        },
        {
          id: 5,
          studentId: '09872388',
          name: 'Morgan Chen',
          status: 'meet',
          score: 87,
          certifications: ['SHRM Passed'],
          performance: {
            overallStatus: 'Meet PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Recruitment & Selection', 
                score: 88, 
                status: 'meet',
                keywords: ['Hiring', 'Interviewing', 'Selection Methods'],
                evidence: [
                  { type: 'Recruitment Strategy', score: '87/100' },
                  { type: 'Interview Assignment', score: '89/100' },
                  { type: 'Case Study', score: '88%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Performance Management', 
                score: 86, 
                status: 'meet',
                keywords: ['Evaluation', 'Feedback', 'Performance Improvement'],
                evidence: [
                  { type: 'Evaluation System Design', score: '85%' },
                  { type: 'Feedback Exercise', score: '87/100' },
                  { type: 'Case Analysis', score: '86%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Compensation & Benefits', 
                score: 87, 
                status: 'meet',
                keywords: ['Salary', 'Benefits', 'Incentives'],
                evidence: [
                  { type: 'Compensation Plan', score: '88%' },
                  { type: 'Benefits Analysis', score: '86%' },
                  { type: 'Final Exam', score: '87/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Employment Law', 
                score: 88, 
                status: 'meet',
                keywords: ['Legal Compliance', 'Regulations', 'Employee Rights'],
                evidence: [
                  { type: 'Legal Analysis', score: '89%' },
                  { type: 'Compliance Report', score: '87%' },
                  { type: 'Case Law Review', score: '88%' }
                ]
              }
            ]
          },
          skills: {
            'Recruiting': 0.88,
            'Performance Evaluation': 0.86,
            'Compensation Planning': 0.87,
            'Legal Compliance': 0.88,
            'Employee Relations': 0.85,
            'Training & Development': 0.86
          }
        }
      ]
    },
    'BSM-BADM314': {
      courseName: 'Supply Chain Management',
      stats: {
        achievementPercentage: 79,
        certificationImpact: "+16%"
      },
      students: [
        {
          id: 1,
          studentId: '09872384',
          name: 'Alex Johnson',
          status: 'meet',
          score: 83,
          certifications: ['CSCP In Progress'],
          performance: {
            overallStatus: 'Meet PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Supply Chain Strategy', 
                score: 84, 
                status: 'meet',
                keywords: ['Strategic Planning', 'Supply Networks', 'Global Sourcing'],
                evidence: [
                  { type: 'Strategy Plan', score: '83/100' },
                  { type: 'Network Analysis', score: '85/100' },
                  { type: 'Case Study', score: '84%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Logistics Management', 
                score: 82, 
                status: 'meet',
                keywords: ['Transportation', 'Warehousing', 'Distribution'],
                evidence: [
                  { type: 'Logistics Plan', score: '81%' },
                  { type: 'Route Optimization', score: '83/100' },
                  { type: 'Case Analysis', score: '82%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Inventory Management', 
                score: 83, 
                status: 'meet',
                keywords: ['Forecasting', 'Stock Management', 'JIT'],
                evidence: [
                  { type: 'Inventory System', score: '84%' },
                  { type: 'Forecasting Analysis', score: '82%' },
                  { type: 'Final Exam', score: '83/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Procurement', 
                score: 84, 
                status: 'meet',
                keywords: ['Sourcing', 'Vendor Management', 'Negotiation'],
                evidence: [
                  { type: 'Sourcing Strategy', score: '85%' },
                  { type: 'Vendor Assessment', score: '83%' },
                  { type: 'Negotiation Exercise', score: '84%' }
                ]
              }
            ]
          },
          skills: {
            'Supply Chain Strategy': 0.84,
            'Logistics Planning': 0.82,
            'Inventory Control': 0.83,
            'Procurement': 0.84,
            'Risk Management': 0.81,
            'Analytics': 0.82
          }
        },
        {
          id: 2,
          studentId: '09872385',
          name: 'Sam Williams',
          status: 'exceed',
          score: 91,
          certifications: ['CSCP Passed', 'Six Sigma Green Belt'],
          performance: {
            overallStatus: 'Exceed PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Supply Chain Strategy', 
                score: 92, 
                status: 'exceed',
                keywords: ['Strategic Planning', 'Supply Networks', 'Global Sourcing'],
                evidence: [
                  { type: 'Strategy Plan', score: '91/100' },
                  { type: 'Network Analysis', score: '93/100' },
                  { type: 'Case Study', score: '92%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Logistics Management', 
                score: 90, 
                status: 'exceed',
                keywords: ['Transportation', 'Warehousing', 'Distribution'],
                evidence: [
                  { type: 'Logistics Plan', score: '89%' },
                  { type: 'Route Optimization', score: '91/100' },
                  { type: 'Case Analysis', score: '90%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Inventory Management', 
                score: 91, 
                status: 'exceed',
                keywords: ['Forecasting', 'Stock Management', 'JIT'],
                evidence: [
                  { type: 'Inventory System', score: '92%' },
                  { type: 'Forecasting Analysis', score: '90%' },
                  { type: 'Final Exam', score: '91/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Procurement', 
                score: 93, 
                status: 'exceed',
                keywords: ['Sourcing', 'Vendor Management', 'Negotiation'],
                evidence: [
                  { type: 'Sourcing Strategy', score: '94%' },
                  { type: 'Vendor Assessment', score: '92%' },
                  { type: 'Negotiation Exercise', score: '93%' }
                ]
              }
            ]
          },
          skills: {
            'Supply Chain Strategy': 0.92,
            'Logistics Planning': 0.90,
            'Inventory Control': 0.91,
            'Procurement': 0.93,
            'Risk Management': 0.89,
            'Analytics': 0.90
          }
        },
        {
          id: 3,
          studentId: '09872386',
          name: 'Jamie Lee',
          status: 'meet',
          score: 74,
          certifications: ['None'],
          performance: {
            overallStatus: 'Meet PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Supply Chain Strategy', 
                score: 73, 
                status: 'meet',
                keywords: ['Strategic Planning', 'Supply Networks', 'Global Sourcing'],
                evidence: [
                  { type: 'Strategy Plan', score: '72/100' },
                  { type: 'Network Analysis', score: '74/100' },
                  { type: 'Case Study', score: '73%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Logistics Management', 
                score: 75, 
                status: 'meet',
                keywords: ['Transportation', 'Warehousing', 'Distribution'],
                evidence: [
                  { type: 'Logistics Plan', score: '76%' },
                  { type: 'Route Optimization', score: '74/100' },
                  { type: 'Case Analysis', score: '75%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Inventory Management', 
                score: 74, 
                status: 'meet',
                keywords: ['Forecasting', 'Stock Management', 'JIT'],
                evidence: [
                  { type: 'Inventory System', score: '73%' },
                  { type: 'Forecasting Analysis', score: '75%' },
                  { type: 'Final Exam', score: '74/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Procurement', 
                score: 72, 
                status: 'meet',
                keywords: ['Sourcing', 'Vendor Management', 'Negotiation'],
                evidence: [
                  { type: 'Sourcing Strategy', score: '71%' },
                  { type: 'Vendor Assessment', score: '73%' },
                  { type: 'Negotiation Exercise', score: '72%' }
                ]
              }
            ]
          },
          skills: {
            'Supply Chain Strategy': 0.73,
            'Logistics Planning': 0.75,
            'Inventory Control': 0.74,
            'Procurement': 0.72,
            'Risk Management': 0.73,
            'Analytics': 0.74
          }
        },
        {
          id: 4,
          studentId: '09872387',
          name: 'Taylor Rodriguez',
          status: 'atRisk',
          score: 64,
          certifications: ['None'],
          performance: {
            overallStatus: 'At Risk',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Supply Chain Strategy', 
                score: 62, 
                status: 'atRisk',
                keywords: ['Strategic Planning', 'Supply Networks', 'Global Sourcing'],
                evidence: [
                  { type: 'Strategy Plan', score: '61/100' },
                  { type: 'Network Analysis', score: '63/100' },
                  { type: 'Case Study', score: '62%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Logistics Management', 
                score: 65, 
                status: 'atRisk',
                keywords: ['Transportation', 'Warehousing', 'Distribution'],
                evidence: [
                  { type: 'Logistics Plan', score: '66%' },
                  { type: 'Route Optimization', score: '64/100' },
                  { type: 'Case Analysis', score: '65%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Inventory Management', 
                score: 64, 
                status: 'atRisk',
                keywords: ['Forecasting', 'Stock Management', 'JIT'],
                evidence: [
                  { type: 'Inventory System', score: '63%' },
                  { type: 'Forecasting Analysis', score: '65%' },
                  { type: 'Final Exam', score: '64/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Procurement', 
                score: 63, 
                status: 'atRisk',
                keywords: ['Sourcing', 'Vendor Management', 'Negotiation'],
                evidence: [
                  { type: 'Sourcing Strategy', score: '62%' },
                  { type: 'Vendor Assessment', score: '64%' },
                  { type: 'Negotiation Exercise', score: '63%' }
                ]
              }
            ]
          },
          skills: {
            'Supply Chain Strategy': 0.62,
            'Logistics Planning': 0.65,
            'Inventory Control': 0.64,
            'Procurement': 0.63,
            'Risk Management': 0.61,
            'Analytics': 0.64
          }
        },
        {
          id: 5,
          studentId: '09872388',
          name: 'Morgan Chen',
          status: 'meet',
          score: 79,
          certifications: ['CSCP In Progress'],
          performance: {
            overallStatus: 'Meet PLO Requirements',
            learningGoals: [
              { 
                id: 'MF Shared LG 1',
                name: 'Supply Chain Strategy', 
                score: 78, 
                status: 'meet',
                keywords: ['Strategic Planning', 'Supply Networks', 'Global Sourcing'],
                evidence: [
                  { type: 'Strategy Plan', score: '77/100' },
                  { type: 'Network Analysis', score: '79/100' },
                  { type: 'Case Study', score: '78%' }
                ]
              },
              { 
                id: 'MF Shared LG 2',
                name: 'Logistics Management', 
                score: 80, 
                status: 'meet',
                keywords: ['Transportation', 'Warehousing', 'Distribution'],
                evidence: [
                  { type: 'Logistics Plan', score: '81%' },
                  { type: 'Route Optimization', score: '79/100' },
                  { type: 'Case Analysis', score: '80%' }
                ]
              },
              { 
                id: 'MF Shared LG 3',
                name: 'Inventory Management', 
                score: 79, 
                status: 'meet',
                keywords: ['Forecasting', 'Stock Management', 'JIT'],
                evidence: [
                  { type: 'Inventory System', score: '78%' },
                  { type: 'Forecasting Analysis', score: '80%' },
                  { type: 'Final Exam', score: '79/100' }
                ]
              },
              { 
                id: 'MF Shared LG 4',
                name: 'Procurement', 
                score: 78, 
                status: 'meet',
                keywords: ['Sourcing', 'Vendor Management', 'Negotiation'],
                evidence: [
                  { type: 'Sourcing Strategy', score: '77%' },
                  { type: 'Vendor Assessment', score: '79%' },
                  { type: 'Negotiation Exercise', score: '78%' }
                ]
              }
            ]
          },
          skills: {
            'Supply Chain Strategy': 0.78,
            'Logistics Planning': 0.80,
            'Inventory Control': 0.79,
            'Procurement': 0.78,
            'Risk Management': 0.77,
            'Analytics': 0.79
          }
        }
      ]
    }
  };

  useEffect(() => {
    // Simulate API call to fetch PLO data
    const fetchPLOData = async () => {
      try {
        // In a real app, you would fetch data here
        // const response = await fetch(`/api/plo-details/${selectedPLO.id}/${selectedCourse}`);
        // const data = await response.json();
        
        // For now, we'll use the mock data
        setTimeout(() => {
          setLoading(false);
          // Set the first student as selected by default
          if (coursesData[selectedCourse]?.students.length > 0 && !selectedStudent) {
            setSelectedStudent(coursesData[selectedCourse].students[0]);
            // Initialize the first learning goal as expanded
            if (coursesData[selectedCourse].students[0]?.performance?.learningGoals?.length > 0) {
              setExpandedLearningGoals({
                [coursesData[selectedCourse].students[0].performance.learningGoals[0].id]: true
              });
            }
          }
        }, 500);
      } catch (error) {
        console.error('Error fetching PLO data:', error);
        setLoading(false);
      }
    };

    fetchPLOData();
    
    // Set the CSS variables for accessible colors
    document.documentElement.style.setProperty('--certification-bg-color', accessibleColors.certificationColors.background);
    document.documentElement.style.setProperty('--certification-text-color', accessibleColors.certificationColors.text);
    document.documentElement.style.setProperty('--mastery-pill-bg-color', accessibleColors.masteryPillColors.background);
    document.documentElement.style.setProperty('--mastery-pill-text-color', accessibleColors.masteryPillColors.text);
    document.documentElement.style.setProperty('--chart-bg-color', accessibleColors.chartColors.background);
    document.documentElement.style.setProperty('--chart-border-color', accessibleColors.chartColors.border);
    
    // Cleanup function to reset variables on component unmount
    return () => {
      document.documentElement.style.removeProperty('--certification-bg-color');
      document.documentElement.style.removeProperty('--certification-text-color');
      document.documentElement.style.removeProperty('--mastery-pill-bg-color');
      document.documentElement.style.removeProperty('--mastery-pill-text-color');
      document.documentElement.style.removeProperty('--chart-bg-color');
      document.documentElement.style.removeProperty('--chart-border-color');
    };
  }, [selectedCourse, selectedStudent, selectedPLO]);

  const handleCourseChange = (courseId) => {
    setSelectedCourse(courseId);
    setSelectedStudent(null); // Reset selected student when course changes
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    // Reset expanded learning goals when selecting a new student
    setExpandedLearningGoals({});
    // Reset info text visibility
    setShowInfoText({});
  };

  // New search and pagination functions
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Filter students based on search term
  const getFilteredStudents = () => {
    const students = coursesData[selectedCourse]?.students || [];
    if (!searchTerm) return students;
    
    return students.filter(student => 
      student.studentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getStatusLabel(student.status).toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Get paginated students
  const getPaginatedStudents = () => {
    const filteredStudents = getFilteredStudents();
    const startIndex = (currentPage - 1) * studentsPerPage;
    const endIndex = startIndex + studentsPerPage;
    return filteredStudents.slice(startIndex, endIndex);
  };

  // Calculate total pages
  const getTotalPages = () => {
    const filteredStudents = getFilteredStudents();
    return Math.ceil(filteredStudents.length / studentsPerPage);
  };

  const handlePageChange = (page) => {
    // Check if selected student will be on the new page before changing
    if (selectedStudent) {
      const filteredStudents = getFilteredStudents();
      const startIndex = (page - 1) * studentsPerPage;
      const endIndex = startIndex + studentsPerPage;
      const newPageStudents = filteredStudents.slice(startIndex, endIndex);
      
      const isSelectedStudentVisible = newPageStudents.some(student => student.id === selectedStudent.id);
      if (!isSelectedStudentVisible) {
        setSelectedStudent(null);
      }
    }
    
    setCurrentPage(page);
  };

  const toggleLearningGoal = (goalId) => {
    setExpandedLearningGoals(prev => ({
      ...prev,
      [goalId]: !prev[goalId]
    }));
  };

  const toggleInfoText = (goalId) => {
    setShowInfoText(prev => ({
      ...prev,
      [goalId]: !prev[goalId]
    }));
  };

  const toggleHeaderExpansion = () => {
    setIsHeaderExpanded(!isHeaderExpanded);
  };

  const getStatusColor = (status, isBackground = false) => {
    // Use different color sets for text vs background use
    const colorSet = isBackground 
      ? accessibleColors.statusBackgroundColors 
      : accessibleColors.statusTextColors;
      
    switch (status) {
      case 'exceed':
        return colorSet.exceed;
      case 'meet':
        return colorSet.meet;
      case 'atRisk':
        return colorSet.atRisk;
      default:
        return '#757575'; // Gray - already has good contrast
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'exceed':
        return 'Exceeds';
      case 'meet':
        return 'Meets';
      case 'atRisk':
        return 'At Risk';
      default:
        return 'N/A';
    }
  };

  // Prepare radar chart data
  const getRadarChartData = (skills) => {
    return {
      labels: Object.keys(skills),
      datasets: [
        {
          label: 'Student Skills',
          data: Object.values(skills).map(value => value * 100),
          backgroundColor: accessibleColors.chartColors.background,
          borderColor: accessibleColors.chartColors.border,
          borderWidth: 2,
          pointBackgroundColor: accessibleColors.chartColors.point,
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: accessibleColors.chartColors.pointHover
        }
      ]
    };
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false // We're using our custom legend
      },
      tooltip: {
        backgroundColor: accessibleColors.chartColors.tooltip.background,
        titleColor: accessibleColors.chartColors.tooltip.titleColor,
        bodyColor: accessibleColors.chartColors.tooltip.bodyColor,
        borderColor: accessibleColors.chartColors.tooltip.borderColor,
        borderWidth: 1,
        padding: 10,
        boxPadding: 5
      }
    },
    scales: {
      r: {
        angleLines: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        pointLabels: {
          font: {
            size: 12
          },
          color: '#555'
        },
        suggestedMin: 0,
        suggestedMax: 100,
        ticks: {
          backdropColor: 'transparent',
          color: '#666',
          stepSize: 20
        }
      }
    }
  };

  if (loading) {
    return <div className="loading">Loading PLO data...</div>;
  }

  const ploDescription = selectedPLO?.name || 'Data-driven Decision Making';

  return (
    <div className="dashboard student-outcomes-details-plo">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb">
        <div className="breadcrumb-item" onClick={onBack}>
          {selectedProgram?.name || 'Program'} - Students Outcomes
        </div>
        <div className="breadcrumb-separator">&gt;</div>
        <div className="breadcrumb-item active">
          PLO: {selectedPLO?.id || 'LG1'}
        </div>
      </div>
      
      {/* Header with PLO Title - Collapsible */}
      <div className="plo-header">
        <h1>
          <span className="plo-badge">{selectedPLO?.id || 'LG1'}</span>
          <div className="plo-description-container">
            <span className="plo-description" id="plo-description">
              {isHeaderExpanded ? ploDescription : truncateText(ploDescription)}
            </span>
            <button 
              className="expand-toggle-button" 
              onClick={toggleHeaderExpansion}
              aria-expanded={isHeaderExpanded}
              aria-controls="plo-description"
            >
              {isHeaderExpanded ? "Show Less" : "Show More"}
            </button>
          </div>
        </h1>
        <button className="back-button" onClick={onBack}>
          <img src="/back-arrow.svg" alt="Back" />
          Back
        </button>
      </div>
      
      {/* Course Selection Filter */}
      <div className="course-filters">
        {Object.keys(coursesData).map(courseId => (
          <div 
            key={courseId}
            className={`course-filter ${selectedCourse === courseId ? 'active' : ''}`}
            onClick={() => handleCourseChange(courseId)}
            title={`${courseId}: ${coursesData[courseId]?.courseName}`}
          >
            <input 
              type="radio" 
              id={courseId} 
              name="course" 
              checked={selectedCourse === courseId}
              onChange={() => {}} // Handled by the div onClick
            />
            <label htmlFor={courseId}>{courseId}</label>
          </div>
        ))}
      </div>

      {/* Course Details with Achievement Metrics */}
      <div className="course-details">
        {selectedCourse && (
          <>
            <div className="course-header">
              {/* Course icon temporarily commented out
              <div className="course-icon">
                <img src="/course-icon.svg" alt="Course" />
              </div>
              */}
              <h2>{selectedCourse}</h2>
            </div>
            <div className="course-metrics">
              <p className="metric-item">
                <strong>Overall Achievement:</strong> {coursesData[selectedCourse]?.stats.achievementPercentage}% students met or exceed expected learning outcomes (Grade {getLetterGrade(coursesData[selectedCourse]?.stats.achievementPercentage)}).
              </p>
              <p className="metric-item">
                <strong>Certification Impact:</strong> students who completed this course have {coursesData[selectedCourse]?.stats.certificationImpact.replace('+', '')} higher CFA pass rate.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Main Content Area - Student List and Details */}
      <div className="content-area">
        {/* Left Side - Student List */}
        <div className="student-list-container">
          <h2>Students</h2>
          
          {/* Search Bar */}
          <div className="student-search-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search students..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="student-search-input"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="clear-search-button"
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            {searchTerm && (
              <div className="search-results-info">
                {getFilteredStudents().length} of {coursesData[selectedCourse]?.students.length || 0} students
              </div>
            )}
          </div>
          
          <div className="student-list">
            {getPaginatedStudents().map(student => (
              <div 
                key={student.id}
                className={`student-item ${selectedStudent?.id === student.id ? 'selected' : ''}`}
                onClick={() => handleStudentSelect(student)}
              >
                <div className="student-name">{student.studentId}</div>
                <div 
                  className="student-status"
                  style={{ color: getStatusColor(student.status, false) }}
                >
                  {getStatusLabel(student.status)}
                </div>
                <div className="student-score">{getLetterGrade(student.score)}</div>
                <div 
                  className="status-indicator"
                  style={{ backgroundColor: getStatusColor(student.status, true) }}
                ></div>
              </div>
            ))}
            
            {getFilteredStudents().length === 0 && (
              <div className="no-students-message">
                {searchTerm ? 'No students match your search.' : 'No students found.'}
              </div>
            )}
          </div>
          
          {/* Pagination */}
          <div className="pagination-container">
            <div className="pagination-info">
              Page {currentPage} of {getTotalPages() || 1}
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-button-1 pagination-nav"
                aria-label="Previous page"
              >
                Prev
              </button>
              
              {Array.from({ length: getTotalPages() || 1 }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`pagination-button-1 ${currentPage === page ? 'active' : ''}`}
                  aria-label={`Go to page ${page}`}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === getTotalPages() || getTotalPages() === 0}
                className="pagination-button-1 pagination-nav"
                aria-label="Next page"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Right Side - Student Details */}
        {selectedStudent && (
          <div className="student-details-container">
            <div className="student-profile-header">
              <div className="student-profile-name">Student {selectedStudent.studentId}</div>
              <div 
                className="student-profile-status"
                style={{ backgroundColor: getStatusColor(selectedStudent.status, true) }}
              >
                {selectedStudent.performance.overallStatus}
              </div>
            </div>

            {/* Certifications */}
            <div className="certifications-container">
              <h3>Certifications:</h3>
              <div className="certification-badges">
                {selectedStudent.certifications.map((cert, index) => (
                  <div key={index} className="certification-badge">
                    {cert}
                  </div>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div className="tabs-container">
              <div className="tabs">
                <div 
                  className={`tab ${activeTab === 'performance' ? 'active' : ''}`}
                  onClick={() => setActiveTab('performance')}
                >
                  Performance
                </div>
                <div 
                  className={`tab ${activeTab === 'skills' ? 'active' : ''}`}
                  onClick={() => setActiveTab('skills')}
                >
                  Skills Map
                </div>
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {activeTab === 'performance' && (
                  <div className="performance-tab">
                    <h3>Learning Goals Performance</h3>
                    <div className="learning-goals-detailed-list">
                      {selectedStudent.performance.learningGoals.map((goal, index) => (
                        <div key={index} className="learning-goal-detailed-item">
                          <div 
                            className="learning-goal-header"
                            onClick={() => toggleLearningGoal(goal.id)}
                          >
                            <div className="learning-goal-toggle">
                              <span className={`toggle-icon ${expandedLearningGoals[goal.id] ? 'expanded' : ''}`}>
                                {expandedLearningGoals[goal.id] ? '▼' : '▶'}
                              </span>
                            </div>
                            <div className="learning-goal-title">
                              Learning Goal: {goal.id}
                            </div>
                            <div className="learning-goal-doc">
                              <img src="/document-icon.svg" alt="Document" />
                            </div>
                          </div>
                          
                          {expandedLearningGoals[goal.id] && (
                            <div className="learning-goal-content">
                              <div className="learning-goal-info">
                                <div className="keywords-section">
                                  <div className="keywords-label">
                                    Keywords
                                    <button 
                                      className="info-icon-button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleInfoText(goal.id);
                                      }}
                                      aria-label="Toggle information about keywords"
                                    >
                                      <img src="/info-icon.svg" alt="Info" />
                                    </button>
                                    {showInfoText[goal.id] && (
                                      <span className="info-text">
                                        Assessed in line with AACSB standards
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="learning-goal-table">
                                  <div className="table-header">
                                    <div className="table-cell keywords-column">Keywords</div>
                                    <div className="table-cell mastery-column">Mastery Level</div>
                                    <div className="table-cell evidence-column">Evidence</div>
                                  </div>
                                  
                                  {goal.keywords.map((keyword, kidx) => (
                                    <div key={kidx} className="table-row">
                                      <div className="table-cell keywords-column">{keyword}</div>
                                      <div className="table-cell mastery-column">
                                        <div className="mastery-pill">
                                          {getLetterGrade(goal.score)}
                                        </div>
                                      </div>
                                      <div className="table-cell evidence-column">
                                        <ul className="evidence-list">
                                          {goal.evidence.map((evidence, eidx) => (
                                            <li key={eidx}>
                                              <span className="evidence-type">{evidence.type}: </span>
                                              <span className="evidence-score">{formatEvidenceScore(evidence.score)}</span>
                                            </li>
                                          ))}
                                        </ul>
                                        <button className="source-button">
                                          Source
                                          <img src="/external-link.svg" alt="Source" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'skills' && (
                  <div className="skills-tab">
                    <h3>Skills Radar</h3>
                    <div className="skills-radar-wrapper">
                      <div className="skills-legend">
                        <div className="legend-item">
                          <span className="legend-color"></span>
                          <span>Student Skills</span>
                        </div>
                      </div>
                      <div className="radar-chart-container">
                        <Radar 
                          data={getRadarChartData(selectedStudent.skills)}
                          options={radarOptions}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentOutcomesDetails_Program_PLO; 